// FILE: src/components/ThisWeekCard.js
//
// "This Week" card — pulls the next ~5 activities from the active study plan
// and renders them as a simple checklist. Activities are date-tagged
// (scheduled_for) so we just filter to within the week starting today.

import React from 'react';
import DashCard from './DashCard';
import styles from './ThisWeekCard.module.css';

export default function ThisWeekCard({ activities = [], onViewPlan }) {
  const week = pickWeek(activities, new Date());

  if (week.length === 0) {
    return (
      <DashCard eyebrow="This Week" eyebrowIcon="📅" testId="dashboard-this-week">
        <div className={styles.empty}>No planned activities this week.</div>
        {onViewPlan && (
          <button type="button" className={styles.link} onClick={onViewPlan}>
            Build a plan ›
          </button>
        )}
      </DashCard>
    );
  }

  return (
    <DashCard eyebrow="This Week" eyebrowIcon="📅" testId="dashboard-this-week">
      <ul className={styles.list}>
        {week.map((a) => {
          const date = new Date(a.scheduled_for);
          const dayLabel = Number.isFinite(date.getTime())
            ? date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })
            : a.scheduled_for;
          const done = a.status === 'completed';
          const skipped = a.status === 'skipped';
          return (
            <li key={a.id || `${a.scheduled_for}-${a.activity_type}`} className={styles.row}>
              <span className={styles.day}>{dayLabel}</span>
              <span className={styles.what}>{activityLabel(a)}</span>
              <span className={done ? styles.checkDone : skipped ? styles.checkSkip : styles.checkOpen}>
                {done ? '●' : skipped ? '×' : '○'}
              </span>
            </li>
          );
        })}
      </ul>
      {onViewPlan && (
        <button type="button" className={styles.link} onClick={onViewPlan}>
          View full plan ›
        </button>
      )}
    </DashCard>
  );
}

function pickWeek(activities, now) {
  if (!Array.isArray(activities)) return [];
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(end.getDate() + 7);

  return activities
    .filter((a) => {
      const t = new Date(a.scheduled_for);
      if (!Number.isFinite(t.getTime())) return false;
      return t >= start && t < end;
    })
    .sort((a, b) => new Date(a.scheduled_for) - new Date(b.scheduled_for))
    .slice(0, 5);
}

function activityLabel(a) {
  if (a?.description) return a.description.split(' — ')[0] || a.description;
  const t = (a?.activity_type || 'session').replace(/_/g, ' ');
  return t.charAt(0).toUpperCase() + t.slice(1);
}
